import React, { FC, useEffect, useState } from 'react'
import Modal from '../../common/Modal'
import { Field, Form } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { useTranslation } from 'react-i18next'
import { CompanyExternalApiKey, patchCompanyExternalApiKeyRequest } from '../../../../states/types/setting'
import { updateCompanyExternalApiKey } from '../../../../states/containers/setting/companyExternalApiKeySlice'

interface EditExternalApiKeyModalState {
  isOpen: boolean,
  setIsOpen: (isOpen: boolean) => void,
}

interface Props {
  editExternalApiKeyModalState: EditExternalApiKeyModalState
  companyExternalApiKey: CompanyExternalApiKey
  closeCallback: VoidFunction
  submittionCallback: VoidFunction
  closeable?: boolean
}

const EditExternalApiKeyModal: FC<Props> = (props) => {
  const {
    editExternalApiKeyModalState,
    companyExternalApiKey,
    closeCallback,
    submittionCallback,
    closeable,
  } = props

  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [formState, setFormState] = useState({
    valid: false,
    pristine: true,
    submitting: false
  })


  const handleSubmit = (values: Omit<patchCompanyExternalApiKeyRequest, 'id'>) => {
    dispatch(
      updateCompanyExternalApiKey(
        {
          id: companyExternalApiKey.id,
          label: values.label
        },
        (response) => {
          closeCallback()
          submittionCallback()
        }
      )
    )
  }

  const {
    errors,
  } = useSelector((state: RootState) => (state))

  const renderServerValidateError = (name: string) => (
    errors.formErrors[name]
    && errors.formErrors[name].map((message: string) => (
      <p key={message} className="form-error">
        {t('validate.server_error', { name: name })}
      </p>
    ))
  )

  const initialValues = {
    label: companyExternalApiKey.label,
  }

  const submitForm = () => {
    const form = document.forms.namedItem('modify_external_api_key')
    if (form) {
      form.dispatchEvent(
        new Event('submit', { cancelable: true, bubbles: true })
      )
    }
  }

  return (
    <Modal
      title="APIキーを編集"
      show={editExternalApiKeyModalState.isOpen}
      wrapperClass="setting_external_api_key"
      closeCallback={closeCallback}
      closeable={closeable}
    >
      {/* モーダルボディ */}
      <>
        <div className="api-key-instruction">
          識別用のラベル名のみ編集可能です。
          <br />
          ※APIキーは自動生成のため編集できません。
        </div>

        <Form<Omit<patchCompanyExternalApiKeyRequest, 'id'>>
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validate={values => {
            const errors: Record<string, string> = {}
            if (!values.label || !values.label.trim()) {
              errors.label = t('model.externalApiKey.error.blank', { key: t('model.externalApiKey.label') })
            }
            return errors
          }}
          render={({ handleSubmit, submitting, valid, pristine, errors }) => {
            useEffect(() => {
              setFormState({
                valid,
                pristine,
                submitting
              })
            }, [valid, pristine, submitting])

            return (
              <form name="modify_external_api_key" onSubmit={handleSubmit}>
                <div className="item">
                  <label htmlFor={t('model.externalApiKey.label')}>
                    <div className="title flex">
                      <div className="label red">必須</div>
                      {t('model.externalApiKey.label')}
                    </div>
                  </label>
                  <div className="detail row_1">
                    <Field
                      type="text"
                      name="label"
                      id="label"
                      component="input"
                      className="form-control"
                      required
                    />
                    {errors?.label && (
                      <p key={errors.label} className="form-error">
                        {errors.label}
                      </p>
                    )}
                    {renderServerValidateError('label')}
                  </div>
                </div>
              </form>
            )
          }}
        />
      </>

      {/* モーダルフッター */}
      <>
        <button type="button" className="button cancel" onClick={closeCallback} disabled={!closeable}>
          キャンセル
        </button>
        <button
          type="button"
          className="button"
          onClick={submitForm}
          disabled={!formState.valid || formState.pristine || formState.submitting}
        >
          編集する
        </button>
      </>
    </Modal>
  )
}

EditExternalApiKeyModal.defaultProps = {
  closeable: true,
}

export default EditExternalApiKeyModal
